import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuoteList } from '../../hooks/useQuoteList';

const EmailQuotePage = () => {
  const navigate = useNavigate();
  const { list, totalItems, listCount } = useQuoteList();
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    referralSource: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (list.length === 0) {
      navigate('/');
    }
  }, [list.length, navigate]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidEmail(formData.email)) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Generate selections list
      const selectionsList = list.map((item, index) => 
        `${index + 1}. ${item.brand} • ${item.collection} • ${item.name} • Size: ${item.size} • Feel: ${item.feel} • Qty: ${item.quantity}`
      ).join('\n');

      // Prepare form data using URLSearchParams for proper encoding
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('customer_name', formData.name || 'Not provided');
      formDataToSubmit.append('customer_email', formData.email);
      formDataToSubmit.append('customer_phone', formData.phone || 'Not provided');
      formDataToSubmit.append('referral_source', formData.referralSource || 'None');
      formDataToSubmit.append('mattress_selections', selectionsList);
      formDataToSubmit.append('total_items', totalItems.toString());
      formDataToSubmit.append('number_of_models', listCount.toString());

      console.log('🚀 Submitting to NEW form URL...');
      console.log('📧 Email:', formData.email);
      console.log('📦 Total items:', totalItems);
      console.log('📋 Selections:', selectionsList);
      
      const response = await fetch('https://readdy.ai/api/form/d5252j9n1516q9jm3vd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString()
      });

      console.log('📡 Response status:', response.status);
      const responseText = await response.text();
      console.log('📄 Response:', responseText);

      if (response.ok) {
        console.log('✅ SUCCESS! Email sent to joevgindi@gmail.com');
        
        setShowModal(false);
        setFormData({ name: '', email: '', phone: '', referralSource: '' });
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      } else {
        console.error('❌ Form submission failed:', response.status, responseText);
        throw new Error(`Submission failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Submission error:', error);
      setErrorMessage('Unable to send your selections right now. Please call us directly at 917-757-7737 or try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setFormData({ name: '', email: '', phone: '', referralSource: '' });
    setErrorMessage('');
  };

  if (list.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-900 to-slate-800">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className="cursor-pointer mr-6"
              >
                <img 
                  src="https://static.readdy.ai/image/a0f757b25fe373fa4ee18ed861087727/8c01a07ddce65998266bf96cfef05a86.png"
                  alt="Midnight Mattresses Logo"
                  className="w-24 h-24 object-contain"
                />
              </button>
              <h1 className="text-2xl font-bold text-slate-800">
                Midnight Mattresses
              </h1>
            </div>
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="text-slate-600 hover:text-cyan-600 transition-colors cursor-pointer font-medium"
              >
                ← Back to Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Email Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Email Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-6">
            <h2 className="text-2xl font-bold mb-2">Your Mattress Selections</h2>
            <p className="text-cyan-100">Ready to send your inquiry</p>
          </div>

          {/* Selected Mattresses Display */}
          <div className="p-6">
            <div className="space-y-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Selected Mattresses:</h3>
              
              {list.map((item, index) => (
                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                  <div className="flex items-start space-x-6">
                    {item.image && (
                      <div className="flex-shrink-0">
                        <img 
                          src={item.image}
                          alt={`${item.brand} ${item.name}`}
                          className="w-32 h-24 object-cover rounded-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://via.placeholder.com/300x200/f3f4f6/6b7280?text=Mattress';
                          }}
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-1">
                            {index + 1}. {item.brand} • {item.collection}
                          </h4>
                          <p className="text-gray-700 font-medium mb-2">{item.name}</p>
                          <div className="space-y-1 text-sm text-gray-600">
                            <p><span className="font-medium">Size:</span> {item.size}</p>
                            <p><span className="font-medium">Feel:</span> {item.feel}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm font-medium">
                            Qty: {item.quantity}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-cyan-50 rounded-lg p-4 border-l-4 border-cyan-600 mb-6">
              <h4 className="font-bold text-gray-900 mb-2">Selection Summary</h4>
              <p className="text-gray-700">
                <strong>Total:</strong> {totalItems} mattress{totalItems > 1 ? 'es' : ''} across {listCount} different model{listCount > 1 ? 's' : ''}
              </p>
            </div>

            {/* Form Submission Button */}
            <div className="text-center">
              <button
                onClick={() => setShowModal(true)}
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white py-4 px-8 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg text-lg"
              >
                <i className="ri-send-plane-line mr-2"></i>
                Send My Selections
              </button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Having trouble? Call us directly at <a href="tel:+19177577737" className="text-cyan-600 hover:text-cyan-700 font-medium cursor-pointer">917-757-7737</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Send My Selections</h3>
                <button
                  onClick={handleModalClose}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Read-only selections list */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Your Selections:</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                  {list.map((item, index) => (
                    <div key={item.id} className="text-sm text-gray-700 mb-1">
                      {index + 1}. {item.brand} • {item.collection} • {item.name} • Qty: {item.quantity}
                    </div>
                  ))}
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} data-readdy-form id="mattress-quote-form">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Name (optional)
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Your Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                      placeholder="Enter your email"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  {/* Referral Source Field */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4">
                    <div className="flex items-start mb-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center mr-3">
                        <i className="ri-gift-line text-white text-lg"></i>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-gray-900 mb-1">Referral Program</h4>
                        <p className="text-xs text-gray-600 mb-3">
                          Were you referred by someone? Help them earn a commission by entering their name or company below.
                        </p>
                      </div>
                    </div>
                    <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-1">
                      Who Referred You? (optional)
                    </label>
                    <input
                      type="text"
                      id="referralSource"
                      name="referralSource"
                      value={formData.referralSource}
                      onChange={(e) => setFormData({ ...formData, referralSource: e.target.value })}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm bg-white"
                      placeholder="e.g., John Smith or ABC Company"
                    />
                    <p className="text-xs text-gray-500 mt-1 flex items-center">
                      <i className="ri-information-line mr-1"></i>
                      Your referrer will receive commission based on your purchase
                    </p>
                  </div>
                </div>

                {errorMessage && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{errorMessage}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    type="submit"
                    disabled={!isValidEmail(formData.email) || isSubmitting}
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Submit
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 px-4 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showToast && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <i className="ri-check-line mr-2"></i>
            <span>✅ Sent! We'll contact you shortly.</span>
          </div>
        </div>
      )}

      {/* Midnight Mattresses Logo at bottom */}
      <div className="text-center py-8">
        <img 
          src="https://static.readdy.ai/image/a0f757b25fe373fa4ee18ed861087727/8c01a07ddce65998266bf96cfef05a86.png"
          alt="Midnight Mattresses"
          className="w-32 h-32 object-contain mx-auto opacity-80"
        />
      </div>
    </div>
  );
};

export default EmailQuotePage;